import { useState } from 'react';
import type { ReopenRequestView } from '@pqrs/contracts';
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@xiltrion/ui/alert';
import { Button } from '@xiltrion/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@xiltrion/ui/dialog';
import { Icono } from '@xiltrion/ui/icono';
import { Spinner } from '@xiltrion/ui/spinner';
import { Textarea } from '@xiltrion/ui/textarea';
import { Field } from '@/components/ui';
import { errorMessage, notify } from '@/features/cases/lib';
import { useCancelReopenRequest, useCaseReopenRequests, useCreateReopenRequest, useDecideReopenRequest } from './api';

/** El responsable de un caso cerrado pide reabrirlo para una respuesta complementaria; lo aprueba quien aprueba en su área. */
export function ReopenRequestDialog({ caseId, onHide }: { caseId: string; onHide: () => void }) {
  const create = useCreateReopenRequest(caseId);
  const [reason, setReason] = useState('');
  const valid = reason.trim().length >= 10;
  const submit = () => create.mutate({ reason: reason.trim() }, {
    onSuccess: (r) => { notify('success', 'Reapertura pedida', `${r.approver?.name ?? 'Quien aprueba en el área'} recibe un aviso para aprobarla.`); onHide(); },
  });
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !create.isPending) onHide(); }}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Pedir respuesta complementaria</DialogTitle>
          <DialogDescription>Quien aprueba en tu área recibe la solicitud. Si la aprueba, el expediente se reabre, te llega la tarea de responder y sigue el plazo de la petición original.</DialogDescription>
        </DialogHeader>
        <Field label="Qué faltó responder" htmlFor="reopen-request-reason" required hint="Queda en el índice complementario y en la historia del caso.">
          <Textarea id="reopen-request-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={4} maxLength={1000} autoFocus placeholder="Por ejemplo: no se respondió el punto 3 de la petición, sobre el cobro de la multa." />
        </Field>
        {create.isError && <Alert variant="destructive"><AlertDescription>{errorMessage(create.error)}</AlertDescription></Alert>}
        <DialogFooter>
          <Button variant="ghost" onClick={onHide} disabled={create.isPending}>Cancelar</Button>
          <Button disabled={!valid || create.isPending} onClick={submit}>{create.isPending ? <Spinner /> : <Icono nombre="mdi:message-plus-outline" />} Pedir reapertura</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** La solicitud pendiente, arriba del expediente: quien la decide la aprueba o rechaza ahí; quien la pidió puede cancelarla. */
export function ReopenRequestNotice({ caseId }: { caseId: string }) {
  const q = useCaseReopenRequests(caseId);
  const decide = useDecideReopenRequest(caseId);
  const [dialog, setDialog] = useState<'reject' | 'cancel' | null>(null);
  const r = q.data?.pending;
  if (!r) return null;
  const approve = () => decide.mutate({ id: r.id, approve: true }, { onSuccess: () => notify('success', 'Expediente reabierto', `${r.requestedBy.name} recibe la tarea de responder.`) });
  return (
    <>
      <Alert variant="warning">
        <Icono nombre="mdi:folder-refresh-outline" aria-hidden />
        <AlertTitle>Reapertura para una respuesta complementaria pendiente de aprobación</AlertTitle>
        <AlertDescription>
          <p>{r.requestedBy.name} la pidió: «{r.reason}»</p>
          <p>{r.approver ? `La aprueba ${r.approver.name}` : 'La aprueba quien aprueba en el área'} (o el líder del área). Al aprobarla, el expediente vuelve al paso de responder con el plazo de la petición original.</p>
          {decide.isError && <p className="text-destructive">{errorMessage(decide.error)}</p>}
        </AlertDescription>
        {(r.can.decide || r.can.cancel) && (
          <AlertAction className="flex gap-2">
            {r.can.decide && <Button size="sm" variant="success" disabled={decide.isPending} onClick={approve}>{decide.isPending ? <Spinner /> : <Icono nombre="mdi:check" />} Aprobar</Button>}
            {r.can.decide && <Button size="sm" variant="outline" disabled={decide.isPending} onClick={() => setDialog('reject')}><Icono nombre="mdi:close" /> Rechazar</Button>}
            {r.can.cancel && !r.can.decide && <Button size="sm" variant="outline" onClick={() => setDialog('cancel')}><Icono nombre="mdi:close" /> Cancelar solicitud</Button>}
          </AlertAction>
        )}
      </Alert>
      {dialog && <ReasonDialog caseId={caseId} r={r} kind={dialog} onHide={() => setDialog(null)} />}
    </>
  );
}

/** Rechazar (el motivo llega a quien la pidió) o cancelar (llega a quien debía aprobarla). */
function ReasonDialog({ caseId, r, kind, onHide }: { caseId: string; r: ReopenRequestView; kind: 'reject' | 'cancel'; onHide: () => void }) {
  const decide = useDecideReopenRequest(caseId);
  const cancel = useCancelReopenRequest(caseId);
  const m = kind === 'reject' ? decide : cancel;
  const [text, setText] = useState('');
  const submit = () => {
    const done = { onSuccess: () => { notify('success', kind === 'reject' ? 'Reapertura rechazada' : 'Solicitud de reapertura cancelada'); onHide(); } };
    if (kind === 'reject') decide.mutate({ id: r.id, approve: false, comment: text.trim() }, done);
    else cancel.mutate({ id: r.id, reason: text.trim() }, done);
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !m.isPending) onHide(); }}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{kind === 'reject' ? 'Rechazar la reapertura' : 'Cancelar la solicitud de reapertura'}</DialogTitle>
          <DialogDescription>{kind === 'reject' ? `${r.requestedBy.name} recibe un aviso con el motivo y el expediente sigue cerrado.` : 'Quien debía aprobarla deja de verla como pendiente. Queda en la historia del caso.'}</DialogDescription>
        </DialogHeader>
        <Field label="Motivo" htmlFor="reopen-request-decision" required><Textarea id="reopen-request-decision" value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={kind === 'reject' ? 2000 : 500} autoFocus /></Field>
        {m.isError && <Alert variant="destructive"><AlertDescription>{errorMessage(m.error)}</AlertDescription></Alert>}
        <DialogFooter>
          <Button variant="ghost" onClick={onHide} disabled={m.isPending}>Volver</Button>
          <Button variant="destructive" disabled={text.trim().length < 3 || m.isPending} onClick={submit}>{m.isPending ? <Spinner /> : <Icono nombre="mdi:close" />} {kind === 'reject' ? 'Rechazar reapertura' : 'Cancelar solicitud'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
